import React, { useEffect, useState } from "react";
import {
  AbsoluteFill,
  Audio,
  Easing,
  continueRender,
  delayRender,
  interpolate,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { ThreeCanvas } from "@remotion/three";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

// =========================================================================
// 1. PROFESSIONAL PODCAST STUDIO ENVIRONMENT & PROPS
// =========================================================================
const PodcastStudioEnvironment: React.FC<{ frame: number }> = ({ frame }) => {
  const floorMaterial = new THREE.MeshPhysicalMaterial({
    color: "#06070B",
    roughness: 0.35,
    metalness: 0.25,
    clearcoat: 0.45,
    clearcoatRoughness: 0.15,
  });

  const backWallMaterial = new THREE.MeshStandardMaterial({
    color: "#0F1118",
    roughness: 0.75,
    metalness: 0.05,
  });

  const slatMaterial = new THREE.MeshStandardMaterial({
    color: "#181B24",
    roughness: 0.55,
    metalness: 0.15,
  });

  const deskMaterial = new THREE.MeshPhysicalMaterial({
    color: "#10131B",
    roughness: 0.22,
    metalness: 0.35,
    clearcoat: 0.65,
    clearcoatRoughness: 0.12,
  });

  const deskEdgeGlow = new THREE.MeshStandardMaterial({
    color: "#00E5FF",
    emissive: new THREE.Color("#00E5FF"),
    emissiveIntensity: 0.8,
    roughness: 0.2,
  });

  const micMetalMaterial = new THREE.MeshStandardMaterial({
    color: "#27272A",
    roughness: 0.25,
    metalness: 0.9,
  });

  const micFoamMaterial = new THREE.MeshStandardMaterial({
    color: "#18181B",
    roughness: 0.8,
    metalness: 0.05,
  });

  const onAirBoxMaterial = new THREE.MeshStandardMaterial({
    color: "#0A0A0C",
    roughness: 0.3,
    metalness: 0.8,
  });

  const onAirRedGlow = new THREE.MeshStandardMaterial({
    color: "#EF4444",
    emissive: new THREE.Color("#DC2626"),
    emissiveIntensity: 2.2,
    roughness: 0.2,
  });

  const mixerConsoleMaterial = new THREE.MeshStandardMaterial({
    color: "#181C26",
    roughness: 0.3,
    metalness: 0.6,
  });

  const mixerLedCyan = new THREE.MeshStandardMaterial({
    color: "#00E5FF",
    emissive: new THREE.Color("#00E5FF"),
    emissiveIntensity: 1.5,
  });

  const mixerLedPink = new THREE.MeshStandardMaterial({
    color: "#F43F5E",
    emissive: new THREE.Color("#F43F5E"),
    emissiveIntensity: 1.5,
  });

  const warmWallLight = new THREE.MeshStandardMaterial({
    color: "#FFB066",
    emissive: new THREE.Color("#FF8C1A"),
    emissiveIntensity: 1.2,
    roughness: 0.2,
  });

  // Acoustic Slats
  const slats = [];
  for (let i = -16; i <= 16; i++) {
    slats.push(
      <mesh
        key={`slat-${i}`}
        material={slatMaterial}
        castShadow
        receiveShadow
        position={[i * 0.36, 2.5, -3.8]}
      >
        <boxGeometry args={[0.16, 7.5, 0.1]} />
      </mesh>
    );
  }

  return (
    <group>
      {/* Studio Floor */}
      <mesh
        material={floorMaterial}
        receiveShadow
        position={[0, -0.4, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
      >
        <planeGeometry args={[25, 25]} />
      </mesh>

      {/* Back Wall */}
      <mesh material={backWallMaterial} receiveShadow position={[0, 2.5, -4.0]}>
        <planeGeometry args={[25, 8.5]} />
      </mesh>

      {/* Acoustic Slats */}
      {slats}

      {/* Warm Ambient Wall Light Columns */}
      <mesh material={warmWallLight} position={[-3.4, 2.2, -3.6]}>
        <cylinderGeometry args={[0.035, 0.035, 5.0, 16]} />
      </mesh>
      <mesh material={warmWallLight} position={[3.4, 2.2, -3.6]}>
        <cylinderGeometry args={[0.035, 0.035, 5.0, 16]} />
      </mesh>

      {/* Professional "ON AIR" Studio Sign in Background Center */}
      <group position={[0, 2.85, -3.6]}>
        {/* Black Outer Bezel */}
        <mesh material={onAirBoxMaterial} position={[0, 0, -0.02]}>
          <boxGeometry args={[2.0, 0.62, 0.08]} />
        </mesh>
        {/* Glowing Red Surface */}
        <mesh material={onAirRedGlow} position={[0, 0, 0.02]}>
          <boxGeometry args={[1.82, 0.46, 0.04]} />
        </mesh>
      </group>

      {/* Main Podcast Desk (Wide 5.6 units, comfortably spanning both co-hosts) */}
      <group position={[0, 0.05, 0.6]}>
        <mesh material={deskMaterial} castShadow receiveShadow position={[0, 0, 0]}>
          <boxGeometry args={[5.6, 0.14, 1.3]} />
        </mesh>
        <mesh material={deskEdgeGlow} position={[0, 0.05, 0.64]}>
          <boxGeometry args={[5.5, 0.02, 0.02]} />
        </mesh>
        <mesh material={micMetalMaterial} position={[0, -0.28, 0]}>
          <cylinderGeometry args={[0.4, 0.5, 0.48, 32]} />
        </mesh>
      </group>

      {/* Center Audio Mixer Console on Desk */}
      <group position={[0, 0.18, 0.65]} rotation={[-0.25, 0, 0]}>
        <mesh material={mixerConsoleMaterial} castShadow position={[0, 0, 0]}>
          <boxGeometry args={[1.2, 0.08, 0.7]} />
        </mesh>
        {/* Glowing LED level meters */}
        <mesh material={mixerLedCyan} position={[-0.35, 0.045, -0.05]}>
          <boxGeometry args={[0.08, 0.02, 0.4]} />
        </mesh>
        <mesh material={mixerLedCyan} position={[-0.15, 0.045, -0.05]}>
          <boxGeometry args={[0.08, 0.02, 0.32]} />
        </mesh>
        <mesh material={mixerLedPink} position={[0.15, 0.045, -0.05]}>
          <boxGeometry args={[0.08, 0.02, 0.36]} />
        </mesh>
        <mesh material={mixerLedPink} position={[0.35, 0.045, -0.05]}>
          <boxGeometry args={[0.08, 0.02, 0.44]} />
        </mesh>
      </group>

      {/* --- PROMINENT BROADCAST MICROPHONE 1 (In front of Zui at Left) --- */}
      <group position={[-1.1, 0.36, 0.8]} rotation={[0.15, 0.3, 0]}>
        {/* Table Clamp & Base */}
        <mesh material={micMetalMaterial} castShadow position={[0, -0.42, 0]}>
          <cylinderGeometry args={[0.22, 0.25, 0.06, 24]} />
        </mesh>
        {/* Heavy-duty Scissor Boom Arm */}
        <mesh material={micMetalMaterial} position={[0, -0.14, 0]}>
          <cylinderGeometry args={[0.04, 0.04, 0.54, 16]} />
        </mesh>
        {/* Warm Gold Glowing Shockmount Ring */}
        <mesh material={warmWallLight} position={[0, 0.16, 0]}>
          <torusGeometry args={[0.22, 0.02, 16, 32]} />
        </mesh>
        {/* Chunky Condenser Capsule & Acoustic Foam */}
        <mesh material={micFoamMaterial} castShadow position={[0, 0.16, 0]}>
          <capsuleGeometry args={[0.13, 0.28, 20, 28]} />
        </mesh>
      </group>

      {/* --- PROMINENT BROADCAST MICROPHONE 2 (In front of Bolo at Right) --- */}
      <group position={[1.08, 0.08, 0.78]} rotation={[0.16, -0.52, 0]}>
        {/* Table Clamp & Base */}
        <mesh material={micMetalMaterial} castShadow position={[0, -0.38, 0]}>
          <cylinderGeometry args={[0.18, 0.2, 0.05, 24]} />
        </mesh>
        {/* Heavy-duty Scissor Boom Arm */}
        <mesh material={micMetalMaterial} position={[0, -0.12, 0]}>
          <cylinderGeometry args={[0.03, 0.03, 0.42, 16]} />
        </mesh>
        {/* Amber Glowing Shockmount Ring */}
        <mesh material={warmWallLight} position={[0, 0.12, 0]}>
          <torusGeometry args={[0.16, 0.016, 16, 32]} />
        </mesh>
        {/* Chunky Condenser Capsule & Acoustic Foam */}
        <mesh material={micFoamMaterial} castShadow position={[0, 0.12, 0]}>
          <capsuleGeometry args={[0.09, 0.18, 18, 24]} />
        </mesh>
      </group>
    </group>
  );
};

// =========================================================================
// 2. PROCEDURAL 3D ZUI (LEFT SEAT: X = -2.0, BALANCED PROPORTION)
// =========================================================================
// --- CANONICAL 3D ZUI PET CAT (YELLOW GOLDEN EDITION) ---
const ZuiMesh: React.FC<{
  frame: number;
  fps: number;
  isSpeaking: boolean;
}> = ({ frame, fps, isSpeaking }) => {
  const breathe = Math.sin(frame * 0.1) * 0.035;
  const bobY = Math.sin(frame * 0.12) * 0.04;
  const earTwitchLeft = Math.sin(frame * 0.18) * 0.08;
  const earTwitchRight = Math.cos(frame * 0.14) * 0.06;
  const tailWag = Math.sin(frame * 0.22) * 0.55;
  const pawGesture = Math.sin(frame * 0.35) * 0.35;

  // Real-time lip sync animation
  const mouthOpen = isSpeaking ? (0.45 + 0.5 * Math.sin(frame * 0.6)) : 0;

  // Blinking logic
  const blinkCycle = frame % 90;
  const isBlinking = blinkCycle > 82 && blinkCycle < 88;
  const eyeScaleY = isBlinking ? 0.08 : 1.0;

  // Tactile PBR Materials - Warm Golden Yellow Cat
  const furMaterial = new THREE.MeshPhysicalMaterial({
    color: "#F59E0B",            // Golden Yellow Cat Fur
    roughness: 0.26,
    metalness: 0.02,
    clearcoat: 0.85,
    clearcoatRoughness: 0.08,
    sheen: 0.9,
    sheenColor: new THREE.Color("#FDE68A"),
  });

  const bellyMaterial = new THREE.MeshPhysicalMaterial({
    color: "#FEF3C7",            // Soft Cream Belly Patch
    roughness: 0.35,
    metalness: 0.0,
    clearcoat: 0.6,
  });

  const innerEarMaterial = new THREE.MeshPhysicalMaterial({
    color: "#FFA8BA",            // Blossom Pink Inner Ear
    roughness: 0.3,
    clearcoat: 0.6,
    sheen: 0.8,
    sheenColor: new THREE.Color("#FB7185"),
  });

  const eyeGlossMaterial = new THREE.MeshPhysicalMaterial({
    color: "#080512",            // Deep Glossy Anime Cat Eye
    roughness: 0.02,
    metalness: 0.25,
    clearcoat: 1.0,
    clearcoatRoughness: 0.02,
  });

  const eyeHighlightMaterial = new THREE.MeshStandardMaterial({
    color: "#FFFFFF",
    roughness: 0.08,
    metalness: 0.0,
  });

  const blushMaterial = new THREE.MeshStandardMaterial({
    color: "#FF5E7E",
    emissive: new THREE.Color("#FF2255"),
    emissiveIntensity: 0.35,
    roughness: 0.4,
  });

  const noseMaterial = new THREE.MeshPhysicalMaterial({
    color: "#FF6B8B",            // Soft Pink Button Nose & Mouth
    roughness: 0.22,
    metalness: 0.08,
    clearcoat: 0.8,
  });

  const collarMaterial = new THREE.MeshPhysicalMaterial({
    color: "#DC2626",            // Classic Red Collar
    roughness: 0.18,
    metalness: 0.35,
    clearcoat: 0.9,
  });

  const bellGoldMaterial = new THREE.MeshStandardMaterial({
    color: "#FFD700",            // Polished Golden Jingle Bell
    roughness: 0.1,
    metalness: 0.95,
  });

  const mouthDarkCavityMaterial = new THREE.MeshStandardMaterial({
    color: "#9F1239",            // Deep Ruby Mouth Cavity
    roughness: 0.3,
  });

  const tongueMaterial = new THREE.MeshStandardMaterial({
    color: "#FB7185",
    roughness: 0.2,
  });

  return (
    // Seated at Left Podcast Seat (X: -2.0, Y: 0.65, Z: 0.1), facing right towards Bolo (rotY: 0.5)
    <group position={[-2.0, 0.65 + bobY, 0.1]} scale={[0.85, 0.85, 0.85]} rotation={[0, 0.5, 0]}>
      {/* --- MAIN CAT BODY --- */}
      <group scale={[1 + breathe, 1 - breathe * 0.5, 1 + breathe]}>
        {/* Golden Yellow Chubby Torso */}
        <mesh material={furMaterial} castShadow receiveShadow position={[0, -0.22, 0]} scale={[1.08, 0.98, 1.05]}>
          <sphereGeometry args={[1.0, 36, 36]} />
        </mesh>
        {/* Soft Cream Belly Patch */}
        <mesh material={bellyMaterial} castShadow receiveShadow position={[0, -0.25, 0.8]} scale={[0.82, 0.88, 0.32]}>
          <sphereGeometry args={[0.85, 24, 24]} />
        </mesh>
      </group>

      {/* --- RED CAT COLLAR & GOLDEN BELL --- */}
      <group position={[0, 0.42, 0]} rotation={[0.18, 0, 0]}>
        <mesh material={collarMaterial} castShadow receiveShadow>
          <torusGeometry args={[0.86, 0.1, 16, 36]} />
        </mesh>
        <mesh material={bellGoldMaterial} castShadow receiveShadow position={[0, -0.2, 0.92]}>
          <sphereGeometry args={[0.22, 24, 24]} />
        </mesh>
      </group>

      {/* --- CUTE CHIBI CAT HEAD --- */}
      <group position={[0, 1.18, 0]} rotation={[0, 0, 0.05]}>
        {/* Golden Head Sphere */}
        <mesh material={furMaterial} castShadow receiveShadow scale={[1.18, 1.02, 1.1]}>
          <sphereGeometry args={[1.0, 36, 36]} />
        </mesh>

        {/* --- CAT EARS --- */}
        {/* Left Ear */}
        <group position={[-0.72, 0.95, -0.05]} rotation={[0.05, 0.1, 0.38 + earTwitchLeft]}>
          <mesh material={furMaterial} castShadow receiveShadow scale={[0.5, 0.9, 0.38]}>
            <coneGeometry args={[1, 1.35, 24]} />
          </mesh>
          <mesh material={innerEarMaterial} position={[0, -0.04, 0.12]} scale={[0.34, 0.68, 0.18]}>
            <coneGeometry args={[1, 1.15, 24]} />
          </mesh>
        </group>

        {/* Right Ear */}
        <group position={[0.72, 0.95, -0.05]} rotation={[0.05, -0.1, -0.38 - earTwitchRight]}>
          <mesh material={furMaterial} castShadow receiveShadow scale={[0.5, 0.9, 0.38]}>
            <coneGeometry args={[1, 1.35, 24]} />
          </mesh>
          <mesh material={innerEarMaterial} position={[0, -0.04, 0.12]} scale={[0.34, 0.68, 0.18]}>
            <coneGeometry args={[1, 1.15, 24]} />
          </mesh>
        </group>

        {/* --- BIG GLOSSY ANIME CAT EYES --- */}
        {/* Left Eye */}
        <group position={[-0.42, 0.05, 1.02]} scale={[1, eyeScaleY, 1]}>
          <mesh material={eyeGlossMaterial} castShadow scale={[0.22, 0.26, 0.14]}>
            <sphereGeometry args={[1, 24, 24]} />
          </mesh>
          {/* Main Big Specular Highlight */}
          <mesh material={eyeHighlightMaterial} position={[0.05, 0.08, 0.12]} scale={[0.08, 0.08, 0.04]}>
            <sphereGeometry args={[1, 16, 16]} />
          </mesh>
          {/* Secondary Mini Sparkle */}
          <mesh material={eyeHighlightMaterial} position={[-0.07, -0.07, 0.12]} scale={[0.04, 0.04, 0.03]}>
            <sphereGeometry args={[1, 16, 16]} />
          </mesh>
        </group>

        {/* Right Eye */}
        <group position={[0.42, 0.05, 1.02]} scale={[1, eyeScaleY, 1]}>
          <mesh material={eyeGlossMaterial} castShadow scale={[0.22, 0.26, 0.14]}>
            <sphereGeometry args={[1, 24, 24]} />
          </mesh>
          {/* Main Big Specular Highlight */}
          <mesh material={eyeHighlightMaterial} position={[0.05, 0.08, 0.12]} scale={[0.08, 0.08, 0.04]}>
            <sphereGeometry args={[1, 16, 16]} />
          </mesh>
          {/* Secondary Mini Sparkle */}
          <mesh material={eyeHighlightMaterial} position={[-0.07, -0.07, 0.12]} scale={[0.04, 0.04, 0.03]}>
            <sphereGeometry args={[1, 16, 16]} />
          </mesh>
        </group>

        {/* --- 3D BLUSH CHEEKS --- */}
        <mesh material={blushMaterial} position={[-0.66, -0.15, 0.9]} scale={[0.22, 0.11, 0.05]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
        <mesh material={blushMaterial} position={[0.66, -0.15, 0.9]} scale={[0.22, 0.11, 0.05]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>

        {/* --- PINK BUTTON NOSE --- */}
        <mesh material={noseMaterial} castShadow position={[0, -0.02, 1.12]} scale={[0.08, 0.055, 0.06]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>

        {/* --- CAT SMILE & SPEAKING MOUTH --- */}
        {isSpeaking ? (
          <group position={[0, -0.16, 1.14]} scale={[1, 0.4 + mouthOpen * 0.9, 1]}>
            <mesh material={mouthDarkCavityMaterial} scale={[0.16, 0.14, 0.06]}>
              <sphereGeometry args={[1, 20, 20]} />
            </mesh>
            <mesh material={tongueMaterial} position={[0, -0.06, 0.03]} scale={[0.1, 0.06, 0.04]}>
              <sphereGeometry args={[1, 16, 16]} />
            </mesh>
          </group>
        ) : (
          <group position={[0, -0.14, 1.08]} rotation={[0, 0, Math.PI]}>
            <mesh material={noseMaterial} position={[-0.07, 0, 0]}>
              <torusGeometry args={[0.07, 0.02, 12, 18, Math.PI * 0.9]} />
            </mesh>
            <mesh material={noseMaterial} position={[0.07, 0, 0]}>
              <torusGeometry args={[0.07, 0.02, 12, 18, Math.PI * 0.9]} />
            </mesh>
          </group>
        )}
      </group>

      {/* --- ARMS / PAWS --- */}
      {/* Left Paw (Resting cute on side/belly) */}
      <group position={[-0.92, 0.08, 0.25]} rotation={[0.4, 0, 0.2]}>
        <mesh material={furMaterial} castShadow receiveShadow scale={[0.26, 0.42, 0.26]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
        <mesh material={innerEarMaterial} position={[0, -0.3, 0.06]} scale={[0.15, 0.15, 0.09]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>

      {/* Right Paw (Gesticulating while talking) */}
      <group
        position={[0.95, 0.25, 0.25]}
        rotation={isSpeaking ? [-0.3, 0.1, -0.4 + pawGesture] : [-0.2, 0.2, -0.7]}
      >
        <mesh material={furMaterial} castShadow receiveShadow scale={[0.26, 0.45, 0.26]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
        <mesh material={innerEarMaterial} position={[0, 0.34, 0.04]} scale={[0.16, 0.16, 0.09]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>

      {/* --- FEET / HIND PAWS --- */}
      <group position={[-0.52, -1.02, 0.58]} rotation={[-0.2, 0.2, 0]}>
        <mesh material={furMaterial} castShadow receiveShadow scale={[0.34, 0.2, 0.45]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
        <mesh material={innerEarMaterial} position={[0, 0.07, 0.13]} scale={[0.16, 0.05, 0.18]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>
      <group position={[0.52, -1.02, 0.58]} rotation={[-0.2, -0.2, 0]}>
        <mesh material={furMaterial} castShadow receiveShadow scale={[0.34, 0.2, 0.45]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
        <mesh material={innerEarMaterial} position={[0, 0.07, 0.13]} scale={[0.16, 0.05, 0.18]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>

      {/* --- FLUFFY WAGGING CAT TAIL --- */}
      <group position={[0, -0.45, -0.9]} rotation={[-0.35, tailWag, 0]}>
        <mesh material={furMaterial} castShadow scale={[0.22, 0.32, 0.7]} position={[0, 0.22, -0.22]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
        <mesh material={bellyMaterial} position={[0, 0.46, -0.6]} scale={[0.2, 0.2, 0.2]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>
    </group>
  );
};

// =========================================================================
// 3. PROCEDURAL 3D BOLO (RIGHT SEAT: X = 2.0 - CLEAN & CHARMING)
// =========================================================================
const RoundBallBuddyMesh: React.FC<{
  frame: number;
  fps: number;
  isSpeaking: boolean;
  isShocked: boolean;
}> = ({ frame, fps, isSpeaking, isShocked }) => {
  // Gentle conversational nod
  const nodY = isSpeaking ? Math.sin(frame * 0.28) * 0.04 : 0;
  const bodyLean = isSpeaking ? Math.sin(frame * 0.18) * 0.03 : 0;
  const antennaSway = Math.sin(frame * 0.2) * (isSpeaking ? 0.16 : 0.08);

  // Clean animated mouth
  const mouthOpen = isSpeaking ? (0.35 + 0.55 * Math.sin(frame * 0.55)) : 0;

  // Eyes: Anime blink
  const blinkCycle = (frame + 35) % 85;
  const isBlinking = !isShocked && blinkCycle > 78 && blinkCycle < 84;
  const eyeScaleY = isBlinking ? 0.08 : isShocked ? 1.25 : 1.0;
  const eyeScaleX = isShocked ? 1.15 : 1.0;

  // Paws tapping on desk
  const pawTap = isSpeaking ? Math.sin(frame * 0.3) * 0.03 : 0;

  // Materials
  const bodyMaterial = new THREE.MeshPhysicalMaterial({
    color: "#A855F7",
    roughness: 0.18,
    metalness: 0.08,
    clearcoat: 1.0,
    clearcoatRoughness: 0.05,
    sheen: 0.75,
    sheenColor: new THREE.Color("#F3E8FF"),
  });

  const bellyCreamMaterial = new THREE.MeshPhysicalMaterial({
    color: "#FDF8FF",
    roughness: 0.24,
    metalness: 0.02,
    clearcoat: 0.85,
    clearcoatRoughness: 0.08,
    sheen: 0.6,
    sheenColor: new THREE.Color("#E9D5FF"),
  });

  const cheekBlushMaterial = new THREE.MeshStandardMaterial({
    color: "#FF3366",
    emissive: new THREE.Color("#FF0055"),
    emissiveIntensity: 0.45,
    roughness: 0.3,
  });

  const eyeGlossMaterial = new THREE.MeshStandardMaterial({
    color: "#0F071E",
    roughness: 0.02,
    metalness: 0.3,
  });

  const eyeHighlightMaterial = new THREE.MeshStandardMaterial({
    color: "#FFFFFF",
    roughness: 0.08,
    metalness: 0.0,
  });

  const mouthDarkMaterial = new THREE.MeshStandardMaterial({
    color: "#6B21A8",
    roughness: 0.2,
  });

  const tongueMaterial = new THREE.MeshStandardMaterial({
    color: "#F43F5E",
    roughness: 0.15,
  });

  const antennaStemMaterial = new THREE.MeshStandardMaterial({
    color: "#EC4899",
    roughness: 0.15,
    metalness: 0.4,
  });

  const glowingOrbMaterial = new THREE.MeshStandardMaterial({
    color: isSpeaking ? "#FDE047" : "#FBBF24",
    emissive: new THREE.Color(isSpeaking ? "#FEF08A" : "#F59E0B"),
    emissiveIntensity: isSpeaking ? 1.4 : 0.8,
    roughness: 0.1,
    metalness: 0.6,
  });

  const footMaterial = new THREE.MeshStandardMaterial({
    color: "#9333EA",
    roughness: 0.3,
    metalness: 0.1,
  });

  return (
    // Seated at Right Podcast Seat (X: 2.0, Y: 0.6, Z: 0.1), angled left (rotY: -0.5)
    <group position={[2.0, 0.78 + nodY, 0.1]} rotation={[0.02, -0.5 + bodyLean, 0]}>
      {/* Solid Clean Sphere Body */}
      <mesh material={bodyMaterial} castShadow receiveShadow>
        <sphereGeometry args={[0.95, 48, 48]} />
      </mesh>

      {/* Smooth Cream Belly */}
      <mesh
        material={bellyCreamMaterial}
        castShadow
        receiveShadow
        position={[0, -0.15, 0.74]}
        scale={[0.74, 0.7, 0.3]}
      >
        <sphereGeometry args={[0.9, 32, 32]} />
      </mesh>

      {/* Feet Resting Grounded */}
      <group position={[-0.42, -0.72, 0.4]} rotation={[-0.1, 0.2, 0]}>
        <mesh material={footMaterial} castShadow receiveShadow scale={[0.3, 0.16, 0.38]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
      </group>
      <group position={[0.42, -0.72, 0.4]} rotation={[-0.1, -0.2, 0]}>
        <mesh material={footMaterial} castShadow receiveShadow scale={[0.3, 0.16, 0.38]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
      </group>

      {/* Clean Springy Antenna */}
      <group position={[0, 0.92, 0]} rotation={[0, 0, antennaSway]}>
        <mesh material={antennaStemMaterial} castShadow position={[0, 0.18, 0]}>
          <cylinderGeometry args={[0.04, 0.05, 0.36, 16]} />
        </mesh>
        <mesh material={glowingOrbMaterial} castShadow position={[0, 0.42, 0]}>
          <sphereGeometry args={[0.16, 24, 24]} />
        </mesh>
        <mesh
          material={antennaStemMaterial}
          castShadow
          position={[0, 0.42, 0]}
          rotation={[Math.PI / 3, 0, frame * 0.08]}
        >
          <torusGeometry args={[0.24, 0.025, 12, 24]} />
        </mesh>
      </group>

      {/* Big Glossy Eyes */}
      <group position={[-0.32, 0.14, 0.86]} scale={[eyeScaleX, eyeScaleY, 1]}>
        <mesh material={eyeGlossMaterial} castShadow scale={[0.18, 0.23, 0.12]}>
          <sphereGeometry args={[1, 24, 24]} />
        </mesh>
        <mesh material={eyeHighlightMaterial} position={[-0.05, 0.08, 0.1]} scale={[0.065, 0.065, 0.035]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
        <mesh material={eyeHighlightMaterial} position={[0.05, -0.05, 0.1]} scale={[0.035, 0.035, 0.02]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>

      <group position={[0.32, 0.14, 0.86]} scale={[eyeScaleX, eyeScaleY, 1]}>
        <mesh material={eyeGlossMaterial} castShadow scale={[0.18, 0.23, 0.12]}>
          <sphereGeometry args={[1, 24, 24]} />
        </mesh>
        <mesh material={eyeHighlightMaterial} position={[-0.05, 0.08, 0.1]} scale={[0.065, 0.065, 0.035]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
        <mesh material={eyeHighlightMaterial} position={[0.05, -0.05, 0.1]} scale={[0.035, 0.035, 0.02]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>

      {/* Cheeks */}
      <mesh material={cheekBlushMaterial} position={[-0.54, -0.06, 0.78]} scale={[0.18, 0.1, 0.05]}>
        <sphereGeometry args={[1, 16, 16]} />
      </mesh>
      <mesh material={cheekBlushMaterial} position={[0.54, -0.06, 0.78]} scale={[0.18, 0.1, 0.05]}>
        <sphereGeometry args={[1, 16, 16]} />
      </mesh>

      {/* Clean Animated Mouth (Positioned proudly on face surface at Y: -0.02, Z: 0.985) */}
      {isSpeaking ? (
        <group position={[0, -0.02, 0.985]} scale={[1, 0.5 + mouthOpen * 0.9, 1]}>
          <mesh material={mouthDarkMaterial} scale={[0.18, 0.14, 0.06]}>
            <sphereGeometry args={[1, 20, 20]} />
          </mesh>
          <mesh material={tongueMaterial} position={[0, -0.04, 0.03]} scale={[0.11, 0.06, 0.04]}>
            <sphereGeometry args={[1, 16, 16]} />
          </mesh>
        </group>
      ) : (
        <group position={[0, -0.02, 0.98]}>
          <mesh material={mouthDarkMaterial} scale={[0.12, 0.05, 0.03]}>
            <sphereGeometry args={[1, 18, 18]} />
          </mesh>
        </group>
      )}

      {/* Hands Resting on Desk */}
      <group position={[-0.72, -0.18 + pawTap, 0.65]} rotation={[0.4, 0.2, -0.2]}>
        <mesh material={bellyCreamMaterial} castShadow receiveShadow scale={[0.2, 0.18, 0.24]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
      </group>
      <group position={[0.72, -0.18 - pawTap, 0.65]} rotation={[0.4, -0.2, 0.2]}>
        <mesh material={bellyCreamMaterial} castShadow receiveShadow scale={[0.2, 0.18, 0.24]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
      </group>
    </group>
  );
};

// =========================================================================
// 4. MAIN REMOTION COMPOSITION: REAL MULTICAM PODCAST DIRECTION
// =========================================================================
export const ZuraAutonomousAIPerkenalan: React.FC = () => {
  const { width, height, fps } = useVideoConfig();
  const frame = useCurrentFrame();

  // Dialogue Turn Intervals:
  // Turn 1 (Zui speaking): Frame 8 -> 228
  // Turn 2 (Bolo speaking & reacting): Frame 244 -> 333
  // Turn 3 (Zui speaking): Frame 346 -> 622
  // Turn 4 (Bolo speaking): Frame 633 -> 822
  // Outro: Frame 822 -> 880
  const isZuiSpeaking = (frame >= 8 && frame < 228) || (frame >= 346 && frame < 622);
  const isBoloSpeaking = (frame >= 244 && frame < 333) || (frame >= 633 && frame < 822);
  const isBoloShocked = frame >= 244 && frame < 333;

  // =========================================================================
  // MULTICAM PODCAST DIRECTOR (SMOOTH CINEMATIC ROBOTIC CAMERA SWITCHES)
  // Seamless 8-frame cubic-bezier transitions + subtle natural breathing drift!
  // =========================================================================
  const SHOTS = [
    // 1. Frame 0 - 28: Master Wide Two-Shot (Opening studio overview)
    { start: 0, end: 28, pos: [0, -0.42, -2.5] as [number, number, number], scale: 0.52, rotY: 0 },
    // 2. Frame 28 - 228: Cam A (Zui 3/4 Medium Close-Up, looking across to right)
    { start: 28, end: 228, pos: [1.05, -0.32, -0.85] as [number, number, number], scale: 0.66, rotY: 0 },
    // 3. Frame 228 - 244: Cam C (Master Wide Two-Shot, reaction cut)
    { start: 228, end: 244, pos: [0, -0.42, -2.5] as [number, number, number], scale: 0.52, rotY: 0 },
    // 4. Frame 244 - 335: Cam B (Bolo 3/4 Medium Close-Up, looking across to left)
    { start: 244, end: 335, pos: [-0.95, -0.30, -0.85] as [number, number, number], scale: 0.66, rotY: 0 },
    // 5. Frame 335 - 346: Cam C (Master Wide Two-Shot, Zui jumps in: "Bisa!")
    { start: 335, end: 346, pos: [0, -0.42, -2.5] as [number, number, number], scale: 0.52, rotY: 0 },
    // 6. Frame 346 - 450: Cam A (Zui Medium Close-Up, explaining Autonomous Agent)
    { start: 346, end: 450, pos: [1.05, -0.32, -0.85] as [number, number, number], scale: 0.66, rotY: 0 },
    // 7. Frame 450 - 540: Cam C (Master Wide Two-Shot, gesturing and listening)
    { start: 450, end: 540, pos: [0, -0.42, -2.5] as [number, number, number], scale: 0.52, rotY: 0 },
    // 8. Frame 540 - 624: Cam A (Zui Punch-in Close-Up, physical phone control)
    { start: 540, end: 624, pos: [1.05, -0.30, -0.75] as [number, number, number], scale: 0.70, rotY: 0 },
    // 9. Frame 624 - 633: Cam C (Master Wide Two-Shot, reaction cut)
    { start: 624, end: 633, pos: [0, -0.42, -2.5] as [number, number, number], scale: 0.52, rotY: 0 },
    // 10. Frame 633 - 820: Cam B (Bolo Close-Up, mind blown)
    { start: 633, end: 820, pos: [-0.95, -0.30, -0.85] as [number, number, number], scale: 0.66, rotY: 0 },
    // 11. Frame 820 - 880: Cam C (Master Wide Two-Shot, smiling outro)
    { start: 820, end: 880, pos: [0, -0.42, -2.5] as [number, number, number], scale: 0.52, rotY: 0 },
  ];

  const getStageTransform = (f: number): {
    pos: [number, number, number];
    scale: number;
    rotY: number;
  } => {
    let shotIdx = SHOTS.findIndex((s) => f >= s.start && f < s.end);
    if (shotIdx === -1) shotIdx = SHOTS.length - 1;
    const cur = SHOTS[shotIdx];

    // Subtle living handheld micro-drift (cameraman breathing)
    const driftX = Math.sin(f * 0.05) * 0.012;
    const driftY = Math.cos(f * 0.04) * 0.008;
    // Slow cinematic creeping push-in inside each shot
    const pushIn = (f - cur.start) * 0.00035;

    // Instant clean multicam hard cuts (NO weird room rotation gliding)
    return {
      pos: [cur.pos[0] + driftX, cur.pos[1] + driftY, cur.pos[2] + pushIn],
      scale: cur.scale + pushIn * 0.15,
      rotY: cur.rotY,
    };
  };

  const stage = getStageTransform(frame);

  // High-Retention Viral Podcast Subtitles with Keyword Highlights
  const SUBTITLES = [
    {
      from: 8,
      to: 110,
      speaker: "ZUI",
      color: "#FBBF24",
      dialogue: "Eh Bolo, lu sadar gak sih?",
      highlights: ["Bolo,", "sadar", "gak", "sih?"],
    },
    {
      from: 110,
      to: 228,
      speaker: "ZUI",
      color: "#FBBF24",
      dialogue: "Zaman sekarang AI itu udah bukan cuma disuruh ngetik teks doang!",
      highlights: ["AI", "bukan", "cuma", "ngetik", "teks"],
    },
    {
      from: 244,
      to: 333,
      speaker: "BOLO",
      color: "#C084FC",
      dialogue: "Lah emang bisa apa lagi? Masa iya AI bisa megang HP sendiri?",
      highlights: ["bisa", "apa", "lagi?", "AI", "megang", "HP", "sendiri?"],
    },
    {
      from: 346,
      to: 460,
      speaker: "ZUI",
      color: "#FBBF24",
      dialogue: "Bisa! Sekarang ada Autonomous Agent...",
      highlights: ["Bisa!", "Autonomous", "Agent..."],
    },
    {
      from: 460,
      to: 622,
      speaker: "ZUI",
      color: "#FBBF24",
      dialogue: "...yang beneran bisa buka aplikasi, nge-tap tombol, scroll feed, sampai download file di HP fisik!",
      highlights: ["buka", "aplikasi,", "nge-tap", "tombol,", "scroll", "feed,", "HP", "fisik!"],
    },
    {
      from: 633,
      to: 822,
      speaker: "BOLO",
      color: "#C084FC",
      dialogue: "Gila! Berarti kita gak perlu mantau layar seharian dong? Tinggal suruh dari chat, HP yang kerja sendiri!",
      highlights: ["Gila!", "gak", "perlu", "mantau", "chat,", "HP", "kerja", "sendiri!"],
    },
    {
      from: 822,
      to: 880,
      speaker: "ZURA • AI LAB",
      color: "#FBBF24",
      dialogue: "Follow @zura.ailab untuk eksperimen AI & Future Tech berikutnya!",
      highlights: ["Follow", "@zura.ailab", "Future", "Tech"],
    },
  ];

  const currentSub = SUBTITLES.find((s) => frame >= s.from && frame < s.to);

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#07080B",
        overflow: "hidden",
        fontFamily: "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif",
      }}
    >
      {/* Background BGM */}
      <Audio src={staticFile("bgm-clean.mp3")} volume={0.06} loop />

      {/* Main Dialogue Audio (Mastered V2: Zui Siska Female + Bolo Hendra Male) */}
      <Audio src={staticFile("zura_autonomous_ai_vo_v2.mp3")} volume={1.0} />

      {/* Camera Switch Click SFX */}
      {frame === 25 && <Audio src={staticFile("click-soft.mp3")} volume={0.4} />}
      {frame === 244 && <Audio src={staticFile("click-soft.mp3")} volume={0.5} />}
      {frame === 346 && <Audio src={staticFile("click-soft.mp3")} volume={0.4} />}
      {frame === 633 && <Audio src={staticFile("click-soft.mp3")} volume={0.5} />}
      {frame === 822 && <Audio src={staticFile("chime.mp3")} volume={0.6} />}

      {/* --- 3D CANVAS WITH DETERMINISTIC STAGE TRANSFORM --- */}
      <div style={{ position: "absolute", width: "100%", height: "100%", zIndex: 10 }}>
        <ThreeCanvas
          shadows
          width={width}
          height={height}
          camera={{
            position: [0, 0.45, 5.4],
            fov: 28,
          }}
          onCreated={({ gl }) => {
            gl.shadowMap.enabled = true;
            gl.shadowMap.type = THREE.PCFSoftShadowMap;
            gl.toneMapping = THREE.ACESFilmicToneMapping;
            gl.toneMappingExposure = 1.15;
          }}
        >
          {/* --- CINEMATIC 3D STUDIO LIGHTING --- */}
          {/* Moody Atmospheric Studio Ambient */}
          <ambientLight intensity={0.55} color="#101424" />

          {/* Primary Cinematic Warm Studio Key Spotlight */}
          <directionalLight
            castShadow
            position={[-3.8, 8.5, 5.2]}
            intensity={4.6}
            color="#FFF7EC"
            shadow-mapSize={[2048, 2048]}
            shadow-bias={-0.0002}
            shadow-camera-near={0.5}
            shadow-camera-far={25}
            shadow-camera-left={-7}
            shadow-camera-right={7}
            shadow-camera-top={7}
            shadow-camera-bottom={-7}
          />

          {/* Overhead Hair & Desk Downlight */}
          <directionalLight position={[0, 8.0, 0.5]} intensity={2.6} color="#FFB877" />

          {/* Laser-Sharp Cyan Edge Rim on Zui */}
          <directionalLight position={[-5.8, 5.2, -3.2]} intensity={6.8} color="#FBBF24" />

          {/* Laser-Sharp Neon Magenta Edge Rim on Bolo */}
          <directionalLight position={[5.8, 5.2, -3.2]} intensity={6.2} color="#E879F9" />

          {/* Soft Front Desk Bounce Light */}
          <directionalLight position={[0, -0.4, 2.2]} intensity={1.4} color="#38BDF8" />

          {/* --- ENTIRE STAGE GROUP CONTROLLED BY MULTICAM DIRECTOR --- */}
          <group
            position={stage.pos}
            scale={[stage.scale, stage.scale, stage.scale]}
            rotation={[0, stage.rotY, 0]}
          >
            {/* Podcast Studio Room Environment & Mics */}
            <PodcastStudioEnvironment frame={frame} />

            {/* Zui (Female Speaker) - At Left Podcast Desk */}
            <ZuiMesh frame={frame} fps={fps} isSpeaking={isZuiSpeaking} />

            {/* Bolo (Male Speaker) - At Right Podcast Desk */}
            <RoundBallBuddyMesh
              frame={frame}
              fps={fps}
              isSpeaking={isBoloSpeaking}
              isShocked={isBoloShocked}
            />
          </group>
        </ThreeCanvas>
      </div>

      {/* --- CINEMATIC POST-PROCESSING 2D VIGNETTE --- */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          zIndex: 25,
          background:
            "radial-gradient(circle at 50% 42%, transparent 45%, rgba(4, 5, 10, 0.45) 80%, rgba(1, 2, 4, 0.88) 100%)",
        }}
      />

      {/* --- 2D OVERLAYS & HUD (TOP COMPACT BANNER & HIGH-RETENTION PODCAST SUBTITLES) --- */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 50,
          pointerEvents: "none",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
          paddingTop: 130, // Compact top placement to avoid character faces
          paddingBottom: 450, // Safe zone for TikTok UI (Y: 1370 - 1470)
          boxSizing: "border-box",
        }}
      >
        {/* COMPACT TOP HEADER LOCKUP */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              background: "rgba(18, 20, 28, 0.94)",
              border: "1.5px solid rgba(255, 255, 255, 0.16)",
              borderRadius: 999,
              padding: "8px 24px",
              boxShadow: "0 8px 30px rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(14px)",
            }}
          >
            <div
              style={{
                width: 9,
                height: 9,
                borderRadius: "50%",
                backgroundColor: isZuiSpeaking ? "#FBBF24" : isBoloSpeaking ? "#C084FC" : "#FBBF24",
                boxShadow: `0 0 12px ${isZuiSpeaking ? "#FBBF24" : isBoloSpeaking ? "#C084FC" : "#FBBF24"}`,
              }}
            />
            <span
              style={{
                color: "#F4F4F5",
                fontSize: 16,
                fontWeight: 800,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
              }}
            >
              @ZURA.AILAB • AI TALK SHOW
            </span>
          </div>

          {currentSub && (
            <div
              style={{
                background: "rgba(22, 25, 35, 0.95)",
                border: `2px solid ${currentSub.color}`,
                borderRadius: 999,
                padding: "6px 20px",
                color: currentSub.color,
                fontSize: 15,
                fontWeight: 900,
                letterSpacing: "0.08em",
                boxShadow: `0 0 20px ${currentSub.color}40`,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <div
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  backgroundColor: currentSub.color,
                }}
              />
              {currentSub.speaker}
            </div>
          )}
        </div>

        {/* HIGH-RETENTION PODCAST SUBTITLE CARD (VIRAL GLASSMORPHIC CAPSULE) */}
        {currentSub && (() => {
          const lineAge = frame - currentSub.from;
          const cardScale = interpolate(lineAge, [0, 7], [0.94, 1.0], {
            easing: Easing.bezier(0.16, 1, 0.3, 1),
            extrapolateRight: "clamp",
          });
          const cardOpacity = interpolate(lineAge, [0, 5], [0, 1], {
            extrapolateRight: "clamp",
          });

          // Render dialogue with keyword highlighting
          const words = currentSub.dialogue.split(" ");
          const renderedWords = words.map((w, idx) => {
            const isHighlighted = currentSub.highlights.some(
              (h) => w.toLowerCase().includes(h.toLowerCase().replace(/[^a-z0-9]/g, ""))
            );
            return (
              <span
                key={idx}
                style={{
                  color: isHighlighted ? currentSub.color : "#FFFFFF",
                  fontWeight: isHighlighted ? 900 : 800,
                  textShadow: isHighlighted
                    ? `0 0 20px ${currentSub.color}99, 0 2px 8px rgba(0,0,0,0.9)`
                    : "0 2px 10px rgba(0,0,0,0.85)",
                  marginRight: 8,
                  display: "inline-block",
                }}
              >
                {w}
              </span>
            );
          });

          return (
            <div
              style={{
                width: 900,
                transform: `scale(${cardScale})`,
                opacity: cardOpacity,
                background:
                  "linear-gradient(180deg, rgba(16, 20, 30, 0.94) 0%, rgba(9, 11, 18, 0.96) 100%)",
                border: `2px solid ${currentSub.color}55`,
                borderRadius: 24,
                padding: "24px 34px",
                boxShadow: `0 24px 60px rgba(0, 0, 0, 0.85), 0 0 35px ${currentSub.color}25, inset 0 1px 0 rgba(255, 255, 255, 0.12)`,
                backdropFilter: "blur(28px)",
                display: "flex",
                flexDirection: "column",
                gap: 14,
              }}
            >
              {/* Card Header: Speaker Tag + Live Audio Visualizer Equalizer */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
                  paddingBottom: 10,
                }}
              >
                {/* Speaker Pill Tag */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    background: `${currentSub.color}18`,
                    border: `1px solid ${currentSub.color}66`,
                    padding: "4px 14px",
                    borderRadius: 8,
                  }}
                >
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: currentSub.color,
                      boxShadow: `0 0 8px ${currentSub.color}`,
                    }}
                  />
                  <span
                    style={{
                      color: currentSub.color,
                      fontSize: 13,
                      fontWeight: 900,
                      letterSpacing: "0.08em",
                    }}
                  >
                    {currentSub.speaker}
                  </span>
                </div>

                {/* Live 5-Bar Audio Equalizer Waveform */}
                <div style={{ display: "flex", alignItems: "flex-end", gap: 3.5, height: 16 }}>
                  {[0, 1, 2, 3, 4].map((i) => {
                    const barH = 5 + 11 * Math.abs(Math.sin((frame + i * 9) * 0.45));
                    return (
                      <div
                        key={i}
                        style={{
                          width: 3.5,
                          height: barH,
                          borderRadius: 2,
                          backgroundColor: currentSub.color,
                          boxShadow: `0 0 6px ${currentSub.color}99`,
                        }}
                      />
                    );
                  })}
                </div>
              </div>

              {/* Subtitle Dialogue Body with Vivid Keyword Highlighting */}
              <div
                style={{
                  fontSize: 34,
                  lineHeight: 1.38,
                  textAlign: "center",
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                }}
              >
                {renderedWords}
              </div>
            </div>
          );
        })()}
      </div>
    </AbsoluteFill>
  );
};
